import { useRive, Layout, Fit, RiveFile } from '@rive-app/react-webgl2';
import OpenInRiveContainer from 'components/about/OpenInRiveContainer';
import Avatar from 'components/common/Avatar';
import { HeartIcon } from 'components/common/svgIcons';
import { getCommunityPath, getCommunityProfilePageUrl } from 'lib/community';
import { insertIf, join } from 'lib/helpers';
import Link from 'next/link';
import { HTMLProps, useContext, useEffect, useRef, useState } from 'react';
import { CommunityGridItem as GridItem } from 'types/community';
import RemixBadge from '../shared/RemixBadge';
import styles from './CommunityGridItem.module.css';
import { IsMobileContext } from 'contexts/IsMobile';
import featureFlags from 'lib/featureFlags';

interface Props extends HTMLProps<HTMLDivElement> {
  item: GridItem;
  hideMetadata?: boolean;
  openInRiveFile?: RiveFile | null;
}

type PreviewInRiveParams = {
  urlSlug: string;
  revisionId: number;
  artboard: string;
  animation: string;
};

function previewInRiveLink({
  urlSlug,
  revisionId,
  artboard,
  animation,
}: PreviewInRiveParams) {
  const baseUrl = `${process.env.NEXT_PUBLIC_EDITOR}/preview/${urlSlug}/${revisionId}`;
  const uriArtboard = encodeURIComponent(artboard);
  const uriAnimation = encodeURIComponent(animation);
  const queryString = `mode=animate&artboard=${uriArtboard}&animation=${uriAnimation}`;

  return `${baseUrl}?${queryString}`;
}

export default function CommunityGridItem({
  item,
  hideMetadata = false,
  ...props
}: Props) {
  const [isVisible, setIsVisible] = useState(true);

  const gridItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const callback: IntersectionObserverCallback = ([entry]) => {
      setIsVisible(entry.isIntersecting);
    };

    const options = {
      rootMargin: '100px',
    };

    const observer = new IntersectionObserver(callback, options);
    observer.observe(gridItemRef.current!);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={gridItemRef}>
      {isVisible ? (
        <AnimationGridItem item={item} hideMetadata={hideMetadata} {...props} />
      ) : (
        <PlaceholderGridItem />
      )}
    </div>
  );
}

type RiveImageProps = {
  item: GridItem;
  isHovered: boolean;
};

function RiveImage({ item, isHovered }: RiveImageProps) {
  const { isMobile } = useContext(IsMobileContext);

  const [isReady, setIsReady] = useState<boolean>(false);

  const { rive, RiveComponent } = useRive({
    src: item.fileUrl,
    artboard: item.artboard,
    animations: item.animation,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
    }),
  });

  useEffect(() => {
    if (rive === null) {
      return;
    }

    setIsReady(true);

    if (isHovered) {
      !isMobile && rive && rive.play();
    } else {
      !isMobile && rive && rive.pause();
    }
  }, [isHovered, rive]);

  return (
    <>
      {!isReady && (
        <img
          src={item.thumbnailUrl ?? undefined}
          className={styles.ImageContainer}
        />
      )}
      <RiveComponent />
    </>
  );
}

function AnimationGridItem({
  item,
  hideMetadata = false,
  openInRiveFile,
  ...props
}: Props) {
  const { isMobile } = useContext(IsMobileContext);
  const [isReady, setIsReady] = useState<boolean>(false);

  const [isHovered, setIsHovered] = useState<boolean>(false);

  function onMouseLeave() {
    setIsHovered(false);
  }

  function onMouseEnter() {
    setIsHovered(true);
    setIsReady(true);
  }

  return (
    <li
      className={styles.CommunityGridItem}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Link href={getCommunityPath(item.urlSlug)}>
        <div
          className={join([styles.AnimationContainer, 'checkerboard'])}
          {...props}
        >
          <div className={styles.Animation}>
            {item.isRemix && <RemixBadge />}
            <OpenInRiveContainer
              title={item.title}
              url={previewInRiveLink(item)}
              openInRiveFile={openInRiveFile}
            >
              {featureFlags.showRivOnHover() && !isMobile ? (
                <div
                  className={styles.AnimationWrapper}
                  style={{
                    backgroundImage: `url(${item.thumbnailUrl || ''})`,
                  }}
                >
                  {isReady && <RiveImage item={item} isHovered={isHovered} />}
                </div>
              ) : (
                <img
                  src={item.thumbnailUrl ?? undefined}
                  className={styles.ImageContainer}
                />
              )}
            </OpenInRiveContainer>
          </div>
        </div>
      </Link>
      {!hideMetadata && featureFlags.newAccountsPage() && (
        <CardDetails item={item} />
      )}
      {!hideMetadata && !featureFlags.newAccountsPage() && (
        <div className={styles.ItemDetails}>
          <div className={styles.Avatar}>
            <Avatar data={item.owner} />
          </div>
          <div className={styles.TitleDetails}>
            <div className={styles.Username}>
              <Link href={getCommunityProfilePageUrl(item.owner.username)}>
                {item.owner.username}
              </Link>
            </div>
            {item.owner.isForHire && (
              <div className={styles.ForHireBadge}>For Hire</div>
            )}
            ·<div className={styles.Title}>{item.title}</div>
          </div>
          {item.reactionCount > 0 && (
            <div className={styles.Likes}>
              <HeartIcon />
              {item.reactionCount}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function CardDetails({ item }: { item: GridItem }) {
  return (
    <div className={styles.CardDetails}>
      <div className={styles.CardTitle}>{item.title}</div>
      <div className={styles.CardMeta}>
        <span className={styles.CardAvatar}>
          <Avatar data={item.owner} />
        </span>
        <Link href={getCommunityProfilePageUrl(item.owner.username)}>
          {item.owner.username}
        </Link>
        {item.owner.isForHire && (
          <span className={styles.CardForHire}>For hire</span>
        )}
        {item.reactionCount > 0 && (
          <span className={styles.CardLikes}>
            <HeartIcon />
            {item.reactionCount}
          </span>
        )}
      </div>
    </div>
  );
}

interface PlaceholderProps extends HTMLProps<HTMLDivElement> {
  checkerboard?: boolean;
  hideMetadata?: boolean;
}

export function PlaceholderGridItem({
  checkerboard = true,
  hideMetadata = false,
  ...props
}: PlaceholderProps) {
  return (
    <li className={styles.CommunityGridItem}>
      <div
        className={join([
          styles.AnimationContainer,
          ...insertIf(checkerboard, 'checkerboard'),
        ])}
        {...props}
      >
        <div className={styles.Animation}></div>
      </div>
      {!hideMetadata && (
        <div className={styles.ItemDetails}>
          <div className={styles.Avatar}>
            <div className={styles.AvatarPlaceholder}></div>
          </div>
          <div className={styles.TitleDetails}>
            <div className={styles.Username}>
              <PlaceholderText width={50} />
            </div>
            <div className={styles.Title}>
              <PlaceholderText width={100} />
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

type PlaceholderTextProps = {
  width?: number;
};

export function PlaceholderText({ width = 100 }: PlaceholderTextProps) {
  return <div className={styles.TextPlaceholder} style={{ width }}></div>;
}
